import { Box, Button, Chip, InputAdornment, TextField, Typography } from '@mui/material';
import { LuDownload as Download, LuPlus as Plus, LuSearch as Search, LuSlidersHorizontal as SlidersHorizontal, LuUsers as Users, LuX as X, LuCheck as Check } from 'react-icons/lu';

type Props = {
  title: string;
  subtitle: string;
  query: string;
  requestCount: number;
  canManageMembers: boolean;
  isEditMode: boolean;
  onQueryChange: (value: string) => void;
  onOpenAddMember: () => void;
  onToggleRequests: () => void;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSaveEdit: () => void;
};

export function GroupReportHeader({
  title,
  subtitle,
  query,
  requestCount,
  canManageMembers,
  isEditMode,
  onQueryChange,
  onOpenAddMember,
  onToggleRequests,
  onStartEdit,
  onCancelEdit,
  onSaveEdit
}: Props) {
  return (
    <Box
      sx={{
        mb: 3,
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'stretch', md: 'flex-start' },
        justifyContent: 'space-between',
        gap: 2
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: '#fff',
            boxShadow: '0 10px 24px rgba(102, 126, 234, 0.18)'
          }}
        >
          <Users size={25} />
        </Box>

        <Box>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700, color: '#020617' }}>
            {title}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, fontWeight: 500, color: '#64748b' }}>
            {subtitle}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(240px,290px) repeat(3, auto)' },
          gap: 1.5,
          alignItems: 'center'
        }}
      >
        <TextField
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
          placeholder="Search by name..."
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={18} />
              </InputAdornment>
            )
          }}
          sx={{
            backgroundColor: '#fff',
            borderRadius: 2,
            '& .MuiOutlinedInput-root': { borderRadius: 2, boxShadow: '0 1px 2px rgba(15, 23, 42, 0.05)' }
          }}
        />

        <Button
          onClick={onOpenAddMember}
          variant="outlined"
          disabled={!canManageMembers}
          sx={{
            height: 40,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            borderColor: '#ddd6fe',
            color: '#7c3aed',
            backgroundColor: '#fff'
          }}
          startIcon={<Plus size={17} />}
        >
          Add Member
        </Button>

        <Button
          variant="outlined"
          sx={{
            height: 40,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            borderColor: '#e2e8f0',
            color: '#475569',
            backgroundColor: '#fff'
          }}
          startIcon={<Download size={17} />}
        >
          Export
        </Button>

        <Button
          onClick={onToggleRequests}
          variant="outlined"
          sx={{
            height: 40,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            borderColor: '#e2e8f0',
            color: '#475569',
            backgroundColor: '#fff'
          }}
          startIcon={<Users size={17} />}
        >
          Requests
          {requestCount > 0 && (
            <Box
              component="span"
              sx={{
                ml: 1,
                display: 'inline-flex',
                minWidth: 20,
                height: 20,
                px: 0.75,
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 999,
                backgroundColor: '#f43f5e',
                color: '#fff',
                fontSize: 10,
                fontWeight: 800
              }}
            >
              {requestCount}
            </Box>
          )}
        </Button>

        {canManageMembers && !isEditMode ? (
          <Button
            variant="outlined"
            onClick={onStartEdit}
            sx={{ height: 40, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
            startIcon={<SlidersHorizontal size={17} />}
          >
            Edit Order
          </Button>
        ) : canManageMembers ? (
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              onClick={onCancelEdit}
              sx={{ height: 40, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
              startIcon={<X size={17} />}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={onSaveEdit}
              sx={{
                height: 40,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 700,
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
              }}
              startIcon={<Check size={17} />}
            >
              Save Changes
            </Button>
          </Box>
        ) : null}

        {!canManageMembers && (
          <Chip
            label="Member view only"
            sx={{
              height: 40,
              borderRadius: 2,
              backgroundColor: '#eff6ff',
              color: '#1d4ed8',
              fontWeight: 700
            }}
          />
        )}
      </Box>
    </Box>
  );
}
